import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';

export enum AdjustmentKind {
  None = 0,
  Free = 1,
  Negotiation = 2,
  Index = 3,
  FixedPercentage = 4,
  FeeToBrf = 5,
}

export enum ArticleTimeUnitKind {
  Hour = 0,
  Day = 1,
  Month = 2,
  Quarter = 3,
  Year = 4,
}

export enum AppendixTypeKind {
  SelfDefined = 1,
  RentalSpecification = 2,
}

export function AppendixTypeKindToString(appendixType: AppendixTypeKind): string {
  switch (appendixType) {
    case AppendixTypeKind.SelfDefined:
      return 'APPENDIX.APPENDIX_TYPE_SELF_DEFINED';
    case AppendixTypeKind.RentalSpecification:
      return 'APPENDIX.APPENDIX_TYPE_RENT_SPECIFICATION';
    default:
      throw new Error(UNIT_ERROR);
  }
}

export enum DebtCollectionSupplierKind {
  None = 0,
  Ropo = 1,
  Preventia = 2,
  Profina = 3,
  PsFinanceGroup = 4,
  FastighetsagarnaMittNord = 5,
  ObservaInkasso = 6,
}

export function DebtCollectionSupplierKindToString(debtCollectionSupplier: DebtCollectionSupplierKind): string {
  switch (debtCollectionSupplier) {
    case DebtCollectionSupplierKind.None:
      return 'COMPANY.DEBT_COLLECTION.NONE';
    case DebtCollectionSupplierKind.Ropo:
      return 'COMPANY.DEBT_COLLECTION.ROPO';
    case DebtCollectionSupplierKind.Preventia:
      return 'COMPANY.DEBT_COLLECTION.PREVENTIA';
    case DebtCollectionSupplierKind.Profina:
      return 'COMPANY.DEBT_COLLECTION.PROFINA';
    case DebtCollectionSupplierKind.PsFinanceGroup:
      return 'COMPANY.DEBT_COLLECTION.PSFINANCEGROUP';
    case DebtCollectionSupplierKind.FastighetsagarnaMittNord:
      return 'COMPANY.DEBT_COLLECTION.FASTIGHETSAGARNA_MITTNORD_INKASSO';
    case DebtCollectionSupplierKind.ObservaInkasso:
      return 'COMPANY.DEBT_COLLECTION.OBSERVA_INKASSO';
    default:
      throw new Error(UNIT_ERROR);
  }
}

export enum RentalChangeKind {
  RentalObject = 0,
  LeaseAgreement = 1,
  RentalObjectAndLeaseAgreement = 2,
}

export enum AllowedOnlineTerminationSignatureKind {
  OnlyBankID = 0,
  BankIDAndClickSign = 1,
}

export enum ArticleUnit {
  None = 0,
  Units = 1,
  Kg = 2,
  Gram = 3,
  Kwh = 4,
  Ton = 5,
  Meter = 6,
  Mm = 7,
  Km = 8,
  M2 = 9,
  M3 = 10,
  Litre = 11,
  Hour = 12,
}

export enum CustomPropertyDataType {
  Numeric = 0,
  Text = 1,
  Date = 2,
  Selection = 3,
  Boolean = 4,
}

export enum CustomPropertyVisibilityType {
  None = 0,
  Component = 1 << 0,
  Company = 1 << 1,
  Dwelling = 1 << 2,
  Property = 1 << 3,
  Building = 1 << 4,
  Premises = 1 << 5,
  DwellingAndCompany = Dwelling | Company,
}

export enum DirectDebitStatus {
  NotConfirmed = 0,
  Confirmed = 1,
  TerminationRequested = 2,
  Terminated = 3,
  IncorrectAccountNumber = 4,
}

export enum DueDateAnchorKind {
  PeriodStart = 0,
  PeriodEnd = 1,
}

export enum ExpiresKind {
  InAdvance = 0,
  InThePeriod = 1,
  Aftewards = 2,
}

export enum FormOfLease {
  Lease = 0,
  CooperativeHousing = 1,
  Ownership = 2,
}

export enum InvoicePeriod {
  Month = 1,
  TwoMonths = 2,
  Quarter = 3,
  Tertrial = 4,
  HalfYear = 6,
  Year = 12,
}

export enum PeriodStartMonth {
  January = 1,
  February = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12,
}

export enum RentPaymentKind {
  TotalRent = 0,
  SpecifiedAdditions = 1,
  OwnTemplate = 2,
}

export enum HeatingPaymentKind {
  IncludedInTheRent = 0,
  CostIsAdded = 1,
  IndividualMeasurement = 2,
  SeparateSubscription = 3,
}

export enum ColdWaterPaymentKind {
  IncludedInTheRent = 0,
  IndividualMeasurement = 1,
  AccordingToTemplate = 2,
  CostIsAdded = 3,
  SeparateSubscription = 4,
}

export enum HotWaterPaymentKind {
  IncludedInTheRent = 0,
  IndividualMeasurement = 1,
  AccordingToTemplate = 2,
  CostIsAdded = 3,
  SeparateSubscription = 4,
}

export enum HouseholdElectricityPaymentKind {
  IncludedInTheRent = 0,
  IndividualMeasurement = 1,
  SeparateSubscription = 2,
  AccordingToTemplate = 3,
}

export enum ParkingElectricityPaymentKind {
  IncludedInTheRent = 0,
  IndividualMeasurement = 1,
  PartOfTheCommonElectricityConsumption = 2,
  SpecifiedInSpecificTerms = 3,
}

export enum LeaseAgreementStatus {
  Draft = 1,
  Coming = 2,
  Valid = 3,
  Terminated = 4,
  Expired = 5,
  Voided = 6,
}

export enum TerminationMonitoringStatus {
  All = 0,
  OneMonth = 1,
  TwoMonths = 2,
  ThreeMonths = 3,
  FourMonths = 4,
  FiveMonths = 5,
  SixMonths = 6,
  SevenMonths = 7,
  EightMonths = 8,
  NineMonths = 9,
  TenMonths = 10,
  ElevenMonths = 11,
  TwelveMonths = 12,
}

export enum LettingStatusKind {
  Coming = 0,
  CurrentPublication = 1,
  PublicationExpired = 2,
  ActiveOffers = 3,
  Agreement = 4,
  Closed = 5,
  OfferPeriodExpired = 6,
  Published = 7,
  NotSpecified = 98,
}

export enum EsignatureErrandStatus {
  AwaitingSignature = 1,
  Signed = 2,
  Cancelled = 3,
  Expired = 4,
  Rejected = 5,
  AwaitingCompanyRepresentativeSignature = 6,
}

export enum CreditorResponsibility {
  Full = 0,
  Limited = 1,
}

export enum LeaseAgreementCategory {
  Dwelling = 0,
  Premises = 1,
  Parking = 2,
  Other = 3,
  Block = 4,
  CooperativeHousing = 5,
}

export enum LeaseAgreementSubCategoryKind {
  None = 0,
  BlockLeaseAgreementPremises = 1,
}

export enum LeasePeriodKind {
  UntilFurtherNotice = 0,
  AutomaticExtension = 1,
  LimitedPeriod = 2,
}

export enum TerminatedByKind {
  Tenant = 0,
  Landlord = 1,
}

export enum NoticePeiodTimeUnitKind {
  Day = 0,
  Week = 1,
  Month = 2,
  Year = 3,
}

export enum OrganizationOfTenantsKind {
  None = 0,
  TenantsAssociation = 1,
  OtherAssociation = 2,
}

export enum RentalChangesUnit {
  Persentage = 0,
  PerSquareMeter = 1,
  PerYear = 2,
}

export enum RentalObjectCategory {
  Dwelling = 0,
  Premises = 1,
  Parking = 2,
  Other = 3,
}

export enum SubleaseRequestDecision {
  NotDecided = 0,
  Approved = 1,
  Rejected = 2,
}

export enum SubleaseStatus {
  None = 0,
  Valid = 1,
  Coming = 2,
  Expired = 3,
}

export enum TenantCategory {
  PhysicalPerson = 0,
  Company = 1,
}

export enum TenantState {
  Registered = 0,
  Active = 1,
  Blocked = 2,
  Locked = 3,
}

export enum AreaType {
  AGAR = 0,
  ATEMP = 1,
  BRA = 2,
  BIA = 3,
  BOA = 4,
  LOA = 5,
  OVA = 6,
  BTA = 7,
  NTA = 12,
  LA = 15,
}

export enum SendMessageType {
  Mail = 1,
  Email = 2,
  Sms = 3,
}

export enum PropertyTaxCalculationBasis {
  Area = 0,
  Rent = 1,
  Percentage = 2,
}

export enum PropertyTaxCalculationMethod {
  PerProperty = 0,
  PerBuilding = 1,
}

export enum PropertyTaxPaidBy {
  Landlord = 0,
  Tenant = 1,
}

export enum PropertyTaxRetroDebitOption {
  RDFAWithVAT = 0,
  RDFANoVAT = 1,
  FASKWithVAT = 2,
  FASKNoVAT = 3,
}

export enum PropertyTaxRetroDebitPeriod {
  None = 0,
  ThreeMonths = 1,
  SixMonths = 2,
}

export enum PropertyTaxType {
  None = 0,
  /// <summary>
  /// Fastighetstyp 1 – Lantbruksenheter
  /// </summary>
  PropertyType_1_Agricultural_Units = 1,
  /// <summary>
  /// Fastighetstyp 2 – Småhusenheter
  /// </summary>
  PropertyType_2_Single_Dwelling_Units = 2,
  /// <summary>
  /// Fastighetstyp 3 – Hyreshusenheter
  /// </summary>
  PropertyType_3_Multi_Dwelling_Building_Units = 3,
  /// <summary>
  /// Fastighetstyp 4 – Industrienheter
  /// </summary>
  PropertyType_4_Industrial_Units = 4,
  /// <summary>
  /// Fastighetstyp 5 – Ägarlägenhetsenheter
  /// </summary>
  PropertyType_5_Condominium_Units = 5,
  /// <summary>
  /// Fastighetstyp 6 – Täkter
  /// </summary>
  PropertyType_6_Extraction_Sites_Quarries = 6,
  /// <summary>
  /// Fastighetstyp 7 – Elproduktionsenheter
  /// </summary>
  PropertyType_7_Power_Production_Units = 7,
  /// <summary>
  /// Fastighetstyp 8 – Specielenheter (skatte- och avgiftsfria)
  /// </summary>
  PropertyType_8_Special_Units_Tax_And_Fee_Free = 8,
}

export function PropertyTaxTypeToString(type: PropertyTaxType): string {
  switch (type) {
    case PropertyTaxType.None:
      return 'PROPERTY_TAX.TYPE.NONE';
    case PropertyTaxType.PropertyType_1_Agricultural_Units:
      return 'PROPERTY_TAX.TYPE.AGRICULTURAL_UNITS';
    case PropertyTaxType.PropertyType_2_Single_Dwelling_Units:
      return 'PROPERTY_TAX.TYPE.SINGLE_DWELLING_UNITS';
    case PropertyTaxType.PropertyType_3_Multi_Dwelling_Building_Units:
      return 'PROPERTY_TAX.TYPE.MULTI_DWELLING_BUILDING_UNITS';
    case PropertyTaxType.PropertyType_4_Industrial_Units:
      return 'PROPERTY_TAX.TYPE.INDUSTRIAL_UNITS';
    case PropertyTaxType.PropertyType_5_Condominium_Units:
      return 'PROPERTY_TAX.TYPE.CONDOMINIUM_UNITS';
    case PropertyTaxType.PropertyType_6_Extraction_Sites_Quarries:
      return 'PROPERTY_TAX.TYPE.EXTRACTION_SITES_QUARRIES';
    case PropertyTaxType.PropertyType_7_Power_Production_Units:
      return 'PROPERTY_TAX.TYPE.POWER_PRODUCTION_UNITS';
    case PropertyTaxType.PropertyType_8_Special_Units_Tax_And_Fee_Free:
      return 'PROPERTY_TAX.TYPE.SPECIAL_UNITS_TAX_AND_FEE_FREE';
  }
}

export function PropertyTaxRetroDebitPeriodToString(period: PropertyTaxRetroDebitPeriod): string {
  switch (period) {
    case PropertyTaxRetroDebitPeriod.None:
      return 'PROPERTY_TAX.NO';
    case PropertyTaxRetroDebitPeriod.ThreeMonths:
      return 'PROPERTY_TAX.3_MONTHS';
    case PropertyTaxRetroDebitPeriod.SixMonths:
      return 'PROPERTY_TAX.6_MONTHS';
  }
}

export function PropertyTaxRetroDebitOptionToString(option: PropertyTaxRetroDebitOption): string {
  switch (option) {
    case PropertyTaxRetroDebitOption.RDFAWithVAT:
      return 'PROPERTY_TAX.RDFA_WITH_VAT';
    case PropertyTaxRetroDebitOption.RDFANoVAT:
      return 'PROPERTY_TAX.RDFA_NO_VAT';
    case PropertyTaxRetroDebitOption.FASKWithVAT:
      return 'PROPERTY_TAX.FASK_WITH_VAT';
    case PropertyTaxRetroDebitOption.FASKNoVAT:
      return 'PROPERTY_TAX.FASK_NO_VAT';
  }
}

export function PropertyTaxCalculationBasisToString(calcBasis: PropertyTaxCalculationBasis): string {
  switch (calcBasis) {
    case PropertyTaxCalculationBasis.Area:
      return 'PROPERTY_TAX.CALCULATION_BASIS_AREA';
    case PropertyTaxCalculationBasis.Rent:
      return 'PROPERTY_TAX.CALCULATION_BASIS_RENT';
    case PropertyTaxCalculationBasis.Percentage:
      return 'PROPERTY_TAX.CALCULATION_BASIS_PERCENTAGE';
  }
}

export function PropertyTaxCalculationMethodToString(calcMethod: PropertyTaxCalculationMethod): string {
  switch (calcMethod) {
    case PropertyTaxCalculationMethod.PerProperty:
      return 'PROPERTY_TAX.CALCULATION_METHOD_PER_PROPERTY';
    case PropertyTaxCalculationMethod.PerBuilding:
      return 'PROPERTY_TAX.CALCULATION_METHOD_PER_BUILDING';
  }
}

export function PropertyTaxPaidByToString(paidBy: PropertyTaxPaidBy): string {
  switch (paidBy) {
    case PropertyTaxPaidBy.Landlord:
      return 'PROPERTY_TAX.PAID_BY_LANDLORD';
    case PropertyTaxPaidBy.Tenant:
      return 'PROPERTY_TAX.PAID_BY_TENANT';
  }
}

export function AreaTypeToString(type: AreaType): string {
  switch (type) {
    case AreaType.AGAR:
      return 'AREA.AGAR';

    case AreaType.ATEMP:
      return 'AREA.ATEMP';

    case AreaType.BIA:
      return 'AREA.BIA';

    case AreaType.BOA:
      return 'AREA.BOA';

    case AreaType.BRA:
      return 'AREA.BRA';

    case AreaType.LOA:
      return 'AREA.LOA';

    case AreaType.BTA:
      return 'AREA.BTA';

    case AreaType.NTA:
      return 'AREA.NTA';

    case AreaType.OVA:
      return 'AREA.OVA';

    case AreaType.LA:
      return 'AREA.LA';
  }
}

const UNIT_ERROR = 'Invalid entry unit.';

export function AdjustmentKindToString(adjustment: AdjustmentKind): string {
  switch (adjustment) {
    case AdjustmentKind.FixedPercentage:
      return 'CONST.FIXED_PERCENTAGE';
    case AdjustmentKind.Free:
      return 'CONST.ADJUSTMENT_FEE';
    case AdjustmentKind.Index:
      return 'CONST.ADJUSTMENT_INDEX';
    case AdjustmentKind.Negotiation:
      return 'CONST.ADJUSTMENT_NEGOTIATION';
    case AdjustmentKind.None:
      return 'CONST.ADJUSTMENT_NONE';
    case AdjustmentKind.FeeToBrf:
      return 'CONST.ADJUSTMENT_FEE_TO_BRF';
    default:
      throw new Error(UNIT_ERROR);
  }
}

export function ArticleTimeUnitKindToString(timeUnit: ArticleTimeUnitKind): string {
  switch (timeUnit) {
    case ArticleTimeUnitKind.Day:
      return 'CONST.TIME_UNIT_DAY';
    case ArticleTimeUnitKind.Hour:
      return 'CONST.TIME_UNIT_HOUR';
    case ArticleTimeUnitKind.Month:
      return 'CONST.TIME_UNIT_MONTH';
    case ArticleTimeUnitKind.Quarter:
      return 'CONST.TIME_UNIT_QUATER';
    case ArticleTimeUnitKind.Year:
      return 'CONST.TIME_UNIT_YEAR';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function ArticleUnitKindToString(unit: ArticleUnit): string {
  switch (unit) {
    case ArticleUnit.Gram:
      return 'CONST.UNIT_GRAM';
    case ArticleUnit.Kg:
      return 'CONST.UNIT_KG';
    case ArticleUnit.Km:
      return 'CONST.UNIT_KM';
    case ArticleUnit.Kwh:
      return 'CONST.UNIT_KWH';
    case ArticleUnit.Litre:
      return 'CONST.UNIT_LITRE';
    case ArticleUnit.M2:
      return 'CONST.UNIT_M2';
    case ArticleUnit.M3:
      return 'CONST.UNIT_M3';
    case ArticleUnit.Meter:
      return 'CONST.UNIT_METER';
    case ArticleUnit.Mm:
      return 'CONST.UNIT_MM';
    case ArticleUnit.None:
      return 'CONST.UNIT_NONE';
    case ArticleUnit.Ton:
      return 'CONST.UNIT_TON';
    case ArticleUnit.Units:
      return 'CONST.UNIT_UNITS';
    case ArticleUnit.Hour:
      return 'CONST.UNIT_HOUR';
    default:
      throw new Error(UNIT_ERROR);
  }
}

export function CustomPropertyTypeToString(dataType: CustomPropertyDataType): string {
  switch (dataType) {
    case CustomPropertyDataType.Boolean:
      return 'CUSTOM_PROPERTY.DATATYPE.BOOLEAN';
    case CustomPropertyDataType.Date:
      return 'CUSTOM_PROPERTY.DATATYPE.DATE';
    case CustomPropertyDataType.Numeric:
      return 'CUSTOM_PROPERTY.DATATYPE.NUMERIC';
    case CustomPropertyDataType.Selection:
      return 'CUSTOM_PROPERTY.DATATYPE.SELECTION';
    case CustomPropertyDataType.Text:
      return 'CUSTOM_PROPERTY.DATATYPE.TEXT';
    default:
      return 'NO_TEXT';
  }
}

export function DirectDebitStatusToString(status: DirectDebitStatus) {
  if (status === undefined) {
    return 'NO_TEXT';
  }

  switch (status) {
    case DirectDebitStatus.Confirmed:
      return 'CONST.DIRECT_DEBIT_STATUS_CONFIRMED';

    case DirectDebitStatus.NotConfirmed:
      return 'CONST.DIRECT_DEBIT_STATUS_NOT_CONFIRMED';

    case DirectDebitStatus.Terminated:
      return 'CONST.DIRECT_DEBIT_STATUS_TERMINATED';

    case DirectDebitStatus.TerminationRequested:
      return 'CONST.DIRECT_DEBIT_STATUS_TERMINATION_REQUESTED';

    case DirectDebitStatus.IncorrectAccountNumber:
      return 'CONST.DIRECT_DEBIT_STATUS_INCORRECT_ACCOUNT_NUMBER';
  }
}

export function DueDateAnchorKindToString(dueDateAnchor: DueDateAnchorKind): string {
  switch (dueDateAnchor) {
    case DueDateAnchorKind.PeriodEnd:
      return 'CONST.DUE_DATE_ANCHOR_PERIOD_END';
    case DueDateAnchorKind.PeriodStart:
      return 'CONST.DUE_DATE_ANCHOR_PERIOD_START';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function ExpiresKindToString(expires: ExpiresKind): string {
  switch (expires) {
    case ExpiresKind.Aftewards:
      return 'CONST.EXPIRES_AFTERWARDS';
    case ExpiresKind.InAdvance:
      return 'CONST.EXPIRES_IN_ADVANCE';
    case ExpiresKind.InThePeriod:
      return 'CONST_EXPIRES_IN_THE_PERIOD';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function AllowedOnlineTerminationSignatureKindToString(allowedKinds: AllowedOnlineTerminationSignatureKind): string {
  switch (allowedKinds) {
    case AllowedOnlineTerminationSignatureKind.OnlyBankID:
      return 'CONST.ALLOWED_TERMINATION_BANKID';
    case AllowedOnlineTerminationSignatureKind.BankIDAndClickSign:
      return 'CONST.ALLOWED_TERMINATION_BANKID_AND_CLICKSIGN';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function FormOfLeaseToString(formOfLease: FormOfLease): string {
  switch (formOfLease) {
    case FormOfLease.CooperativeHousing:
      return 'RENTAL_OBJECT.FORMS_OF_LEASE.COOPERATIVE_HOUSING';
    case FormOfLease.Lease:
      return 'RENTAL_OBJECT.FORMS_OF_LEASE.LEASE';
    case FormOfLease.Ownership:
      return 'RENTAL_OBJECT.FORMS_OF_LEASE.OWNERSHIP';
  }
}

export function InvoicePeriodToString(invoicePeriod: InvoicePeriod): string {
  switch (invoicePeriod) {
    case InvoicePeriod.HalfYear:
      return 'CONST.INVOICE_PERIOD_HALF_YEAR';
    case InvoicePeriod.Month:
      return 'CONST.INVOICE_PERIOD_MONTH';
    case InvoicePeriod.Quarter:
      return 'CONST.INVOICE_PERIOD_QUATER';
    case InvoicePeriod.Tertrial:
      return 'CONST.INVOICE_PERIOD_TERTRIAL';
    case InvoicePeriod.TwoMonths:
      return 'CONST.INVOICE_PERIOD_TWO_MONTHS';
    case InvoicePeriod.Year:
      return 'CONST.INVOICE_PERIOD_YEAR';
  }
}

export function PeriodToString(translations: any, startMonth: PeriodStartMonth, periodMonths: number, short?: boolean): string {
  const startMonthName = getMonthName(startMonth, short);
  if (periodMonths === 1) return startMonthName;

  let endMonth = startMonth + periodMonths - 1;
  while (endMonth > 12) endMonth -= 12;
  const endMonthName = getMonthName(endMonth, short);
  return `${getTranslation(translations, startMonthName)} - ${getTranslation(translations, endMonthName)}`;
}

export function HeatingPaymentKindToString(heatingPaymentKind: HeatingPaymentKind, brf?: boolean): string {
  switch (heatingPaymentKind) {
    case HeatingPaymentKind.IncludedInTheRent:
      return brf ? 'CONST.HEATING_PAYMENT_INCLUDED_BRF' : 'CONST.HEATING_PAYMENT_INCLUDED';
    case HeatingPaymentKind.CostIsAdded:
      return 'CONST.HEATING_PAYMENT_ADDED';
    case HeatingPaymentKind.IndividualMeasurement:
      return 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_INDIVIDUALMEASUREMENT';
    case HeatingPaymentKind.SeparateSubscription:
      return brf ? 'CONST.HEATING_PAYMENT_SEPARATESUBSCRIPTION_BRF' : 'CONST.HEATING_PAYMENT_SEPARATESUBSCRIPTION';
  }
}

export function ColdWaterPaymentKindToString(coldWaterPaymentKind: ColdWaterPaymentKind, brf?: boolean): string {
  switch (coldWaterPaymentKind) {
    case ColdWaterPaymentKind.IncludedInTheRent:
      return brf ? 'CONST.COLD_WATER_PAYMENT_INCLUDED_BRF' : 'CONST.COLD_WATER_PAYMENT_INCLUDED';
    case ColdWaterPaymentKind.IndividualMeasurement:
      return 'CONST.COLD_WATER_PAYMENT_INDIVIDUALMEASUREMENT';
    case ColdWaterPaymentKind.AccordingToTemplate:
      return 'CONST.ACCORDING_TO_TEMPLATE';
    case ColdWaterPaymentKind.CostIsAdded:
      return 'CONST.COLD_WATER_PAYMENT_ADDED';
    case ColdWaterPaymentKind.SeparateSubscription:
      return brf ? 'CONST.COLD_WATER_PAYMENT_SEPARATESUBSCRIPTION_BRF' : 'CONST.COLD_WATER_PAYMENT_SEPARATESUBSCRIPTION';
  }
}

export function HotWaterPaymentKindToString(hotWaterPaymentKind: HotWaterPaymentKind, brf?: boolean): string {
  switch (hotWaterPaymentKind) {
    case HotWaterPaymentKind.IncludedInTheRent:
      return brf ? 'CONST.HOT_WATER_PAYMENT_INCLUDED_BRF' : 'CONST.HOT_WATER_PAYMENT_INCLUDED';
    case HotWaterPaymentKind.IndividualMeasurement:
      return 'CONST.HOT_WATER_PAYMENT_INDIVIDUALMEASUREMENT';
    case HotWaterPaymentKind.AccordingToTemplate:
      return 'CONST.ACCORDING_TO_TEMPLATE';
    case HotWaterPaymentKind.CostIsAdded:
      return 'CONST.HEATING_PAYMENT_ADDED';
    case HotWaterPaymentKind.SeparateSubscription:
      return brf ? 'CONST.HOT_WATER_PAYMENT_SEPARATESUBSCRIPTION_BRF' : 'CONST.HOT_WATER_PAYMENT_SEPARATESUBSCRIPTION';
  }
}

export function HouseholdElectricityPaymentKindToString(householdElectricityPaymentKind: HouseholdElectricityPaymentKind, brf?: boolean): string {
  switch (householdElectricityPaymentKind) {
    case HouseholdElectricityPaymentKind.IncludedInTheRent:
      return brf ? 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_INCLUDED_BRF' : 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_INCLUDED';
    case HouseholdElectricityPaymentKind.IndividualMeasurement:
      return 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_INDIVIDUALMEASUREMENT';
    case HouseholdElectricityPaymentKind.SeparateSubscription:
      return brf ? 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_SEPARATESSUBSCRIPTION_BRF' : 'CONST.HOUSEHOLD_ELECTRICITY_PAYMENT_SEPARATESSUBSCRIPTION';
    case HouseholdElectricityPaymentKind.AccordingToTemplate:
      return 'CONST.ACCORDING_TO_TEMPLATE';
  }
}

export function ParkingElectricityPaymentKindToString(parkingElectricityPaymentKind: ParkingElectricityPaymentKind, brf?: boolean): string {
  switch (parkingElectricityPaymentKind) {
    case ParkingElectricityPaymentKind.IncludedInTheRent:
      return brf ? 'CONST.PARKING_ELECTRICITY_PAYMENT_INCLUDED_BRF' : 'CONST.PARKING_ELECTRICITY_PAYMENT_INCLUDED';
    case ParkingElectricityPaymentKind.IndividualMeasurement:
      return 'CONST.PARKING_ELECTRICITY_PAYMENT_INDIVIDUALMEASUREMENT';
    case ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption:
      return 'CONST.PARKING_ELECTRICITY_PAYMENT_PARTOFTHECOMMON';
    case ParkingElectricityPaymentKind.SpecifiedInSpecificTerms:
      return 'CONST.PARKING_ELECTRICITY_PAYMENT_SPECIFIEDINSPECIFICTERMS';
  }
}

export function RentPaymentKindToString(rentPaymentKind: RentPaymentKind, brf?: boolean): string {
  switch (rentPaymentKind) {
    case RentPaymentKind.TotalRent:
      return brf ? 'COOPERATIVE_HOUSING.RENT_PAYMENT_TOTALRENT' : 'CONST.RENT_PAYMENT_TOTALRENT';
    case RentPaymentKind.SpecifiedAdditions:
      return 'CONST.RENT_PAYMENT_ADDITIONS';
    case RentPaymentKind.OwnTemplate:
      return 'CONST.RENT_PAYMENT_OWN_TEMPLATE';
  }
}

export function LeaseAgreementStatusToString(status: LeaseAgreementStatus) {
  switch (status) {
    case LeaseAgreementStatus.Coming:
      return 'CONST.LEASE_AGREEMENT_STATUS_COMING';
    case LeaseAgreementStatus.Draft:
      return 'CONST.LEASE_AGREEMENT_STATUS_DRAFT';
    case LeaseAgreementStatus.Expired:
      return 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED';
    case LeaseAgreementStatus.Terminated:
      return 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED';
    case LeaseAgreementStatus.Valid:
      return 'CONST.LEASE_AGREEMENT_STATUS_VALID';
    case LeaseAgreementStatus.Voided:
      return 'CONST.LEASE_AGREEMENT_STATUS_VOIDED';

    default:
      return ''; //throw new Error(UNIT_ERROR);
  }
}

export function LeasePeriodKindToString(leasePeriod: LeasePeriodKind): string {
  switch (leasePeriod) {
    case LeasePeriodKind.AutomaticExtension:
      return 'CONST.LEASE_PERIOD_AUTOMATIC_EXTENSION';
    case LeasePeriodKind.LimitedPeriod:
      return 'CONST.LEASE_PERIOD_LIMITED_PERIOD';
    case LeasePeriodKind.UntilFurtherNotice:
      return 'CONST.LEASE_PERIOD_UNTIL_FUTHER_NOTICE';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function NoticePeiodTimeUnitKindToString(noticePeriod: NoticePeiodTimeUnitKind): string {
  switch (noticePeriod) {
    case NoticePeiodTimeUnitKind.Day:
      return 'CONST.TIME_UNIT_DAY';
    case NoticePeiodTimeUnitKind.Month:
      return 'CONST.TIME_UNIT_MONTH';
    case NoticePeiodTimeUnitKind.Week:
      return 'CONST.TIME_UNIT_WEEK';
    case NoticePeiodTimeUnitKind.Year:
      return 'CONST.TIME_UNIT_YEAR';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function OrganizationOfTenantsKindToString(kind: OrganizationOfTenantsKind) {
  switch (kind) {
    case OrganizationOfTenantsKind.None:
      return 'CONST.NONE';
    case OrganizationOfTenantsKind.OtherAssociation:
      return 'CONST.ORGANIZATION_OF_TENANTS_OTHER_ASSOCIATION';
    case OrganizationOfTenantsKind.TenantsAssociation:
      return 'CONST.ORGANIZATION_OF_TENANTS_TENANTS_ASSOCIATION';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function RentalChangesUnitToString(unit: RentalChangesUnit): string {
  switch (unit) {
    case RentalChangesUnit.Persentage:
      return 'CONST.RENTAL_CHANGES_UNIT_PERCENTAGE';

    case RentalChangesUnit.PerSquareMeter:
      return 'CONST.RENTAL_CHANGES_UNIT_PER_SQUARE_METER';

    case RentalChangesUnit.PerYear:
      return 'CONST.RENTAL_CHANGES_UNIT_PER_YEAR';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function RentalObjectCategoryToString(category: RentalObjectCategory): string {
  switch (category) {
    case RentalObjectCategory.Dwelling:
      return 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING';
    case RentalObjectCategory.Other:
      return 'CONST.RENTAL_OBJECT_CATEGORY_OTHER';
    case RentalObjectCategory.Parking:
      return 'CONST.RENTAL_OBJECT_CATEGORY_PARKING';
    case RentalObjectCategory.Premises:
      return 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function LeaseAgreementCategoryToString(category: LeaseAgreementCategory): string {
  switch (category) {
    case LeaseAgreementCategory.Dwelling:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_DWELLING';
    case LeaseAgreementCategory.Other:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_OTHER';
    case LeaseAgreementCategory.Parking:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_PARKING';
    case LeaseAgreementCategory.Premises:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_PREMISES';
    case LeaseAgreementCategory.CooperativeHousing:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_COOPERATIVE_HOUSING';
    case LeaseAgreementCategory.Block:
      return 'DASHBOARD.MENU.LEASE_AGREEMENT_BLOCK';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function SubleaseRequestDecisionToString(status: SubleaseRequestDecision) {
  switch (status) {
    case SubleaseRequestDecision.NotDecided:
      return 'CONST.SUBLEASE_REQUEST_DECISION_NOTDECIDED';
    case SubleaseRequestDecision.Approved:
      return 'CONST.SUBLEASE_REQUEST_DECISION_APPROVED';
    case SubleaseRequestDecision.Rejected:
      return 'CONST.SUBLEASE_REQUEST_DECISION_REJECTED';
    default:
      return '';
  }
}

export function SubleaseStatusToString(status: SubleaseStatus) {
  switch (status) {
    case SubleaseStatus.None:
      return 'CONST.SUBLEASE_STATUS_NONE';
    case SubleaseStatus.Valid:
      return 'CONST.SUBLEASE_STATUS_VALID';
    case SubleaseStatus.Coming:
      return 'CONST.SUBLEASE_STATUS_COMING';
    case SubleaseStatus.Expired:
      return 'CONST.SUBLEASE_STATUS_EXPIRED';
  }
}

export function TenantCategoryToString(category: TenantCategory): string {
  switch (category) {
    case TenantCategory.Company:
      return 'CONST.TENANT_CATEGORY_COMPANY';

    case TenantCategory.PhysicalPerson:
      return 'CONST.TENANT_CATEGORY_PHYSICAL_PERSON';

    default:
      throw new Error(UNIT_ERROR);
  }
}

export function TenantStateToString(category: TenantState) {
  switch (category) {
    case TenantState.Registered:
      return 'CONST.TENANT_STATE_REGISTERED';
    case TenantState.Active:
      return 'CONST.TENANT_STATE_ACTIVE';
    case TenantState.Blocked:
      return 'CONST.TENANT_STATE_BLOCKED';
    case TenantState.Locked:
      return 'CONST.TENANT_STATE_LOCKED';
    default:
      return '';
  }
}
